import { useState } from 'react';
import Button from '../ui/Button';
import RejectDialog from '../candidates/RejectDialog';
import { pushToast } from '../ui/toastBus';

// Multi-select state for a request list. Only pending rows can be selected — reviewed ones are
// history. `getId` maps a row to its request id.
export function useRowSelection(rows, getId, isPending) {
  const [selected, setSelected] = useState(() => new Set());

  const selectableIds = rows.filter(isPending).map(getId);
  // Filters can hide rows after they were ticked; only ever act on what's still visible.
  const selectedIds = selectableIds.filter((id) => selected.has(id));
  const allSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length;

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));
  const clear = () => setSelected(new Set());

  return {
    selectedIds,
    selectableCount: selectableIds.length,
    allSelected,
    isSelected: (id) => selected.has(id),
    toggle,
    toggleAll,
    clear,
  };
}

// Wraps a request card with a leading checkbox (blank spacer for non-pending rows so cards align).
export function SelectableRow({ id, pending, selection, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-4 shrink-0 pt-5">
        {pending && (
          <input
            type="checkbox"
            checked={selection.isSelected(id)}
            onChange={() => selection.toggle(id)}
            aria-label="Select request"
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
        )}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

// Select-all + "Approve selected / Reject selected" bar. `onApprove(id)` / `onReject(id, reason)`
// are the existing single-request calls; they run one after another so two requests on the same
// candidate can't race, and a failure on one doesn't stop the rest.
export function BulkActionBar({ selection, onApprove, onReject, onDone, noun = 'request', actionableIds, extraActions, selectAllLabel }) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (selection.selectableCount === 0) return null;

  // Approve/reject only apply to the pending ones when the selection also holds reviewed rows.
  const actionable = actionableIds ? selection.selectedIds.filter((id) => actionableIds.has(id)) : selection.selectedIds;
  const count = actionable.length;

  const run = async (label, fn) => {
    setBusy(true);
    let failed = 0;
    let firstError = '';
    for (const id of actionable) {
      try {
        await fn(id);
      } catch (err) {
        failed += 1;
        firstError = firstError || err.response?.data?.message || '';
      }
    }
    setBusy(false);
    setRejectOpen(false);
    selection.clear();
    onDone();
    const ok = count - failed;
    if (failed === 0) {
      pushToast({ type: 'success', message: `${ok} ${noun}${ok === 1 ? '' : 's'} ${label}.` });
    } else {
      pushToast({
        type: 'error',
        message: `${ok} ${label}, ${failed} failed${firstError ? ` — ${firstError}` : ''}.`,
      });
    }
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5">
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={selection.allSelected}
          onChange={selection.toggleAll}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        {selectAllLabel || `Select all pending (${selection.selectableCount})`}
      </label>
      {selection.selectedIds.length > 0 && (
        <>
          <span className="text-sm text-gray-500">{selection.selectedIds.length} selected</span>
          <div className="ml-auto flex gap-2">
            {extraActions}
            {count > 0 && (
              <>
                <Button onClick={() => run('approved', onApprove)} loading={busy && !rejectOpen} disabled={busy}>
                  Approve selected
                </Button>
                <Button variant="danger" onClick={() => setRejectOpen(true)} disabled={busy}>
                  Reject selected
                </Button>
              </>
            )}
          </div>
        </>
      )}

      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={(reason) => run('rejected', (id) => onReject(id, reason))}
        loading={busy}
        actionLabel={`Reject ${count} ${noun}${count === 1 ? '' : 's'}`}
      />
    </div>
  );
}
